import React from "react";
import { Typography, Box } from "@mui/material";

export interface HaloPsaGuideInnerProps {
  /** When false, hide the "Setup Guide — Halo PSA" heading (parent supplies a title). */
  showGuideTitle?: boolean;
}

/**
 * Halo PSA setup copy + deep links (new agent / new API app). Shared: Add Integration modal + HaloSetupForm.
 * Grep: Halo PSA setup guide, API-only agent, Client ID Secret
 */
export const HaloPsaGuideInner: React.FC<HaloPsaGuideInnerProps> = ({
  showGuideTitle = true,
}) => {
  return (
    <>
      {showGuideTitle ? (
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 15, color: "#1a1a1a", mb: 1.5 }}>
        Setup Guide — Halo PSA
      </Typography>
      ) : null}
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 14, color: "#1a1a1a", mb: 1 }}>
        URL
      </Typography>
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5, mb: 2 }}>
        Paste the Halo instance URL you open in your browser (same as the <strong>URL</strong> field below), e.g.{" "}
        <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 13 }}>
          https://acme.halopsa.com
        </Typography>
        .
      </Typography>

      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 14, color: "#1a1a1a", mb: 1 }}>
        1. Create an API-only agent
      </Typography>
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5, mb: 1 }}>
        Go to{" "}
        <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 12 }}>
          /config/agents/agents?agentid=-1
        </Typography>{" "}
        on your Halo site.
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2, "& li": { mb: 0.75 } }}>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Create a <strong>New Agent</strong>.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Enter a username (e.g. <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 12 }}>Theo AI</Typography>).
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Set <strong>Roles</strong> to <strong>Administrator</strong>.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Check <strong>Is an API-only Agent</strong>.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Set <strong>Default Team</strong> and <strong>Working Hours</strong>, then save.
        </Typography>
      </Box>

      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 14, color: "#1a1a1a", mb: 1 }}>
        2. Register the API application (Client ID and Secret)
      </Typography>
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5, mb: 1 }}>
        Go to{" "}
        <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 12 }}>
          /config/integrations/api/applications?id=-1
        </Typography>{" "}
        on your Halo site.
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1, "& li": { mb: 0.75 } }}>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          <strong>Application Name</strong>: e.g. <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 12 }}>Theo AI</Typography>.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          <strong>Authentication Method</strong>: <strong>Client ID and Secret (Services)</strong>.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Copy <strong>Client ID</strong> and <strong>Client Secret</strong> into the fields below.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          <strong>Login Type</strong>: <strong>Agent</strong>.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          <strong>Agent to log in as</strong>: the agent you created (e.g.{" "}
          <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 12 }}>Theo AI</Typography>).
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Open the <strong>Permissions</strong> tab and grant these required permissions:
          <Box component="ul" sx={{ m: 0.5, pl: 2.5, "& li": { mb: 0.5 } }}>
            <Typography component="li" sx={{ fontFamily: "monospace", fontSize: 12, color: "#555" }}>
              read:tickets
            </Typography>
            <Typography component="li" sx={{ fontFamily: "monospace", fontSize: 12, color: "#555" }}>
              edit:tickets
            </Typography>
            <Typography component="li" sx={{ fontFamily: "monospace", fontSize: 12, color: "#555" }}>
              read:customers
            </Typography>
            <Typography component="li" sx={{ fontFamily: "monospace", fontSize: 12, color: "#555" }}>
              read:items
            </Typography>
            <Typography component="li" sx={{ fontFamily: "monospace", fontSize: 12, color: "#555" }}>
              read:calendar{" "}
              <Typography component="span" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 11, color: "#777" }}>
                (for the Scheduled Calls / ticket-lifecycle feature)
              </Typography>
            </Typography>
          </Box>
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Click <strong>Save</strong>.
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "#666", lineHeight: 1.5, mt: 1 }}>
        Note: Theo checks these scopes from your OAuth token and validates read access on
        ticket, customer, and item endpoints. Editing ticket permissions are validated from
        the granted scopes.
      </Typography>
    </>
  );
};
